import React, { useEffect } from 'react';
import Divider from 'aquilla/core/atoms/Divider';
import styled from '@emotion/styled';
import { mediaQuery, Spacings } from '@styles/index';
import Dynamic from '@base/Dynamic/Dynamic';
import useGoalInfo from '@api/hooks/goal/useGoalInfo';
import { useRouter } from 'next/router';
import useDst from '@api/hooks/goal/test-series/useDST';
import { useIsMobile } from '@stores/AppCommonData';
import PurchasedBlock from '@cont/StoreListing/PurchasedBlock';
import ShowCase from 'aquilla/core/molecules/ShowCase';
import StoreCard from '@cont/StoreListing/StoreCard';
import pushRoute from '@utils/pushRoute';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { useDispatchToStore } from '@hooks/reduxHooks';
import { fetchBatches } from '@actions/batches';
import { getIsFreeUser, getIsLoggedIn } from '@api/selectors/auth';
import cookies, { readCookie } from '@utils/cookies';
import useExperiments, { setExperimentVariation } from '@stores/experiments';
import { getExperimentVariation } from '@stores/selectors/experiments';
import validateAndSetSessionId, {
  DEVICE_ID_COOKIE_NAME
} from '@utils/session/validateAndSetSessionId';
import { fetchABExperimentData } from '@api/fetchers/experiments';
import { abProxyJWT } from '@utils/abHelper';
import useGoalSpecificCities from '@api/hooks/offlineCentres/useGoalSpecificCities';
import CompeteAwarenessBanner from '@cont/Compete/GoalHome/AwarenessBanner';
import useOfflineCentreStore from '@stores/city-specific-offline-centres';
import ScholarshipBanner from './ScholarshipBanner';
import SubscriptionTop from './SubscriptionTop';
import AllAboutExam from './AllAboutExam';
import OfflineCentres from './OfflineCentres';
import CarouselFeed from './CarouselFeed';
import { getABExperimentBucketing } from './utils';
import useFreeTrial from './FreeTrial/useFreeTrial';
import useFreeTrialStore from './FreeTrial/freeTrialStore';
// import usePresubscribedBlocks from './hooks/usePresubscribedBlocks';
import useActiveSectionStore from './useActiveSectionStore';
import createLoadableVisibilityComponent from './LoadOnVisibleInView';
import FreeTrialModal from './FreeTrialModal';
import { useGoalHomePageInfo } from './context/goalLanding';

const ReferralBanner = Dynamic(() =>
  import('@cont/Settings/Referral/ReferralBanner')
);
const PopularClasses = Dynamic(() => import('./PopularClasses'));
const ClassesBySubject = Dynamic(() => import('./ClassesBySubject'));
const GetSubscription = Dynamic(() => import('./Batch/GetSubscription'));
const HeroBatchSection = Dynamic(() => import('./Batch/HeroBatchSection'));
const EducatorSection = Dynamic(() => import('./EducatorSection'));
const CourseSection = Dynamic(() => import('./Batch/CourseSection'));
const ClassesVideo = Dynamic(() => import('./ClassesVideo'));
const FreeTrial = Dynamic(() => import('./FreeTrial/FreeTrial'));
const UpcomingClasses = Dynamic(() => import('./UpcomingClasses'));
const BatchSection = Dynamic(() => import('./Batch/BatchSection'));
const AppInstallBannerComponent = Dynamic(() => import('./AppInstallBanner'));
const AppInstallBanner = createLoadableVisibilityComponent({
  Component: AppInstallBannerComponent,
  name: 'AppInstallBanner'
});
const BookAVisitBlock = Dynamic(() =>
  import('@cont/CitySpecificOfflineCentres/BookAVisitBlock')
);
const BookAVisit = Dynamic(() =>
  import('@cont/CitySpecificOfflineCentres/BookAVisitDrawer')
);
const VisitBookedForCenter = Dynamic(() =>
  import('@cont/CitySpecificOfflineCentres/VisitBookedForCenter')
);

const Container = styled.div`
  font-size: 1vw;
  line-height: 1em;
`;

const StyledDivider = styled(Divider)`
  width: var(--layout-width);
  margin: auto;
  :last-of-type {
    display: none;
  }
  ${mediaQuery.uptoTablet} {
    width: var(--layout-tab-width);
  }
  ${mediaQuery.uptoMobile} {
    width: 100%;
    padding: 0 16px;
    box-sizing: border-box;
  }
`;

const PurchasedBlockWrapper = styled.div`
  margin-top: ${Spacings.SPACING_6B};
  margin-bottom: ${Spacings.SPACING_6B};
  .presub-purch-block {
    .SeeAll-btn-wrapper {
      padding-right: ${Spacings.SPACING_4B};
    }
    ${mediaQuery.uptoMobile} {
      padding-left: ${Spacings.SPACING_4B};
      & .store-card-wrapper {
        padding-right: ${Spacings.SPACING_4B};
      }
    }
  }
`;

const SecContainer = styled.div`
  width: var(--layout-width);
  margin: ${Spacings.SPACING_12B} auto;
  .store-show-case {
    padding-bottom: ${Spacings.SPACING_12B};
    border-bottom: 1px solid var(--color-i-divider);
    .aquilla-iconbutton {
      display: none;
    }
  }
  ${mediaQuery.uptoMobile} {
    width: 100%;
    margin: ${Spacings.SPACING_8B} auto;
  }
`;

const NewPresubscribed = (props) => {
  const { isBookAVisitFlow, isVisitBooked } = useOfflineCentreStore();
  const { socialProof } = props;
  const dispatch = useDispatchToStore();
  const router = useRouter();
  const { goalUID } = router.query;
  const { goalInfo } = useGoalInfo(goalUID);
  useEffect(() => {
    dispatch(fetchBatches(goalUID));
  }, [goalUID, dispatch]);
  const setSectionInSpot = useActiveSectionStore((state) => state.setSec);
  const { revalidateSubscribedBlocks, blockData } = useGoalHomePageInfo();
  const { dstData: dstBlock } = useDst(goalUID, goalInfo?.name, 'Home');
  const isFreeUser = useMyInfo(getIsFreeUser(goalUID));
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const accessToken = readCookie('accessToken');
  const experimentVariation = useExperiments(
    getExperimentVariation('notify_me_and_follow_educator_component')
  );
  const { hadFreeTrialSubscription } = useFreeTrial(goalUID);
  const isActivateFreeTrialClicked = useFreeTrialStore(
    (storeState) => storeState.buttonClicked
  );
  const { showUpcomingClass, showEducatorFollow } =
    getABExperimentBucketing(experimentVariation);
  useEffect(() => {
    validateAndSetSessionId();
    const deviceID = cookies.readCookie(DEVICE_ID_COOKIE_NAME);
    if (!accessToken || (accessToken && isLoggedIn && isFreeUser)) {
      fetchABExperimentData(
        'notify_me_and_follow_educator_component',
        abProxyJWT,
        deviceID
      );
    } else if (accessToken && isLoggedIn && !isFreeUser) {
      setExperimentVariation('notify_me_and_follow_educator_component', null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, isLoggedIn, isFreeUser]);

  useEffect(() => {
    const hashURI = window.location.hash;
    setSectionInSpot(hashURI);
  }, [setSectionInSpot]);

  const { isCentreAvailable, defaultCentreUid } =
    useGoalSpecificCities(goalUID);

  const PurchasedItem = (block) => {
    const {
      block: { data }
    } = block;
    const isMobile = useIsMobile();
    const redirectToSettings = () => {
      const path = '/settings';
      pushRoute(path, path);
    };

    return isMobile ? (
      <PurchasedBlockWrapper>
        <PurchasedBlock className="presub-purch-block" data={data} />
      </PurchasedBlockWrapper>
    ) : (
      <SecContainer>
        <ShowCase
          title="My purchases"
          itemsPerSlide={4}
          onSeeAll={redirectToSettings}
          className="store-show-case"
          seeAllVariant="ghost-green"
        >
          {data.map((courseInfo) => (
            <StoreCard key={courseInfo} data={courseInfo} includeCta />
          ))}
        </ShowCase>
      </SecContainer>
    );
  };

  const getComponent = (block) => {
    switch (block.type) {
      case 'carousel_feed':
        return CarouselFeed;
      case 'subscription_top':
        return SubscriptionTop;
      case 'free_trial':
        return FreeTrial;
      case 'daily_scholarship_test_web':
        return ScholarshipBanner;
      case 'carousel_classes':
        return ClassesVideo;
      case 'special_classes':
        return PopularClasses;
      case 'upcoming_classes':
        return UpcomingClasses;
      case 'topic_group_classes':
        return ClassesBySubject;
      case 'educators':
        return EducatorSection;
      case 'subscription_plan':
        return GetSubscription;
      case 'hero_batch':
        return HeroBatchSection;
      case 'courses_v2':
        return CourseSection;
      case 'referral_benefit_card':
        return ReferralBanner;
      case 'store_purchased_items':
        return PurchasedItem;
      case 'compete_home_banner':
        return CompeteAwarenessBanner;
      case 'centres_visit_block':
        return BookAVisitBlock;
      default:
        return null;
    }
  };

  const getBlocks = () => {
    const batchSection = {
      subscription_plan: 1,
      hero_batch: 1,
      courses_v2: 1
    };
    const blocks = [];
    const batchBlocks = [];
    let batchSecIndex;

    blockData.results.forEach((block, i) => {
      const Component = getComponent(block);

      if (Component) {
        if (batchSection[block.type]) {
          batchBlocks.push(
            <Component block={block} socialProof={socialProof} />
          );
          blocks.push('temp');
          if (!batchSecIndex) batchSecIndex = i;
        } else if (block.type === 'daily_scholarship_test_web')
          blocks.push(<Component block={dstBlock} socialProof={socialProof} />);
        else if (block.type === 'centres_visit_block') {
          blocks.push(<Component data={block.data[0]} newGoalPage />);
        } else if (block.type === 'carousel_feed') {
          blocks.push(<Component block={block} source="goal" />);
        } else
          blocks.push(
            <Component
              block={block}
              socialProof={socialProof}
              showEducatorFollow={showEducatorFollow}
              showUpcomingClass={showUpcomingClass}
              newGoalPage
              revalidateSubscribedBlocks={revalidateSubscribedBlocks}
            />
          );
      }
    });

    if (isCentreAvailable)
      batchBlocks.push(
        <OfflineCentres data={goalInfo} defaultCentreUid={defaultCentreUid} />
      );

    if (batchSecIndex)
      blocks[batchSecIndex] = (
        <BatchSection>
          {batchBlocks.map((blockComponent) => (
            <>
              {blockComponent}
              <StyledDivider />
            </>
          ))}
        </BatchSection>
      );

    return blocks.filter((block) => block !== 'temp');
  };

  return (
    <Container>
      {getBlocks()}
      <AllAboutExam />
      <AppInstallBanner />
      <FreeTrialModal
        goalInfo={goalInfo}
        isLoggedIn={isLoggedIn}
        isActivateFreeTrialClicked={isActivateFreeTrialClicked}
        hadFreeTrialSubscription={hadFreeTrialSubscription}
      />
      {isBookAVisitFlow ? <BookAVisit newGoalPage /> : null}
      {isVisitBooked ? <VisitBookedForCenter /> : null}
    </Container>
  );
};

export default NewPresubscribed;
